import { env } from '../config/env';

// ActionCable client over native WebSocket. Mirrors SocketManager.kt:
// subscribe to DeviceChannel with device_uuid, 30s heartbeat, exp-backoff
// reconnect, and dispatch namespaced `domain.action` events.

// Canonical event names use a `domain.action` namespace. The rest of the app
// only ever sees these; legacy wire names are normalized away at the boundary.
export type CableMessageType =
  | 'stream.update'
  | 'pairing.complete'
  | 'app.new_version'
  | 'app.force_update';

// Single source of truth for inbound event names: maps every accepted wire name
// to its canonical form. The legacy snake_case names are kept so the backend can
// migrate at its own pace — delete those rows once all senders emit the
// namespaced names.
const EVENT_ALIASES: Record<string, CableMessageType> = {
  'stream.update': 'stream.update',
  'pairing.complete': 'pairing.complete',
  'app.new_version': 'app.new_version',
  'app.force_update': 'app.force_update',

  // Legacy (pre-namespace) — kept for backward compatibility.
  stream_update: 'stream.update',
  pairing_complete: 'pairing.complete',
  new_version: 'app.new_version',
  force_update: 'app.force_update',
};

function normalizeEventType(raw: unknown): CableMessageType | null {
  return typeof raw === 'string' ? (EVENT_ALIASES[raw] ?? null) : null;
}

type MessageHandler = (type: CableMessageType, payload: Record<string, unknown>) => void;

const HEARTBEAT_INTERVAL_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

class CableClient {
  private ws: WebSocket | null = null;
  private uuid = '';
  private connected = false;
  private subscribed = false;
  private reconnectAttempts = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private handlers = new Set<MessageHandler>();
  private manuallyClosed = false;

  onMessage(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  // Connect (or re-subscribe) for the given device uuid.
  connect(uuid: string): void {
    if (uuid) this.uuid = uuid;
    this.manuallyClosed = false;

    if (this.connected && this.ws) {
      this.sendSubscribe();
      return;
    }
    this.open();
  }

  disconnect(): void {
    this.manuallyClosed = true;
    this.stopHeartbeat();
    this.clearReconnect();
    this.ws?.close(1000, 'Closing connection');
    this.ws = null;
    this.connected = false;
    this.subscribed = false;
  }

  private open(): void {
    if (this.ws) return;

    try {
      this.ws = new WebSocket(env.cableUrl);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.connected = true;
      this.reconnectAttempts = 0;
      this.sendSubscribe();
    };

    this.ws.onmessage = (ev) => this.handleRaw(ev.data);

    this.ws.onclose = () => {
      this.connected = false;
      this.subscribed = false;
      this.stopHeartbeat();
      this.ws = null;
      if (!this.manuallyClosed) this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onclose follows; reconnect handled there.
    };
  }

  private sendSubscribe(): void {
    if (!this.connected || !this.ws || !this.uuid) return;
    const identifier = JSON.stringify({ channel: 'DeviceChannel', device_uuid: this.uuid });
    this.ws.send(JSON.stringify({ command: 'subscribe', identifier }));
  }

  private handleRaw(raw: string): void {
    let json: Record<string, unknown>;
    try {
      json = JSON.parse(raw);
    } catch {
      return;
    }

    const type = json['type'] as string | undefined;

    if (type === 'confirm_subscription') {
      this.subscribed = true;
      this.startHeartbeat();
      return;
    }
    if (type === 'ping' || type === 'welcome') return;

    const message = json['message'] as Record<string, unknown> | undefined;
    if (message) {
      const mt = normalizeEventType(message['type']);
      if (mt) this.handlers.forEach((h) => h(mt, message));
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.connected && this.subscribed && this.ws) {
        const identifier = JSON.stringify({ channel: 'DeviceChannel', device_uuid: this.uuid });
        const data = JSON.stringify({ action: 'heartbeat' });
        this.ws.send(JSON.stringify({ command: 'message', identifier, data }));
      } else {
        this.stopHeartbeat();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.reconnectAttempts = 0; // allow future check() retries
      return;
    }
    this.reconnectAttempts++;
    const delay = Math.min(2 ** this.reconnectAttempts * 1000, 60_000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, delay);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // Called periodically by the player to recover a dead connection.
  check(): void {
    if (!this.connected && !this.reconnectTimer && this.uuid && !this.manuallyClosed) {
      this.reconnectAttempts = 0;
      this.open();
    }
  }
}

export const cable = new CableClient();
